import { BTaxCart } from '@/app/modules/btax/services/btax-cart.service';
import { selectBTaxAutoDraftEnabled, selectBTaxBlockMessages, selectBTaxDisplayPrintBillButton, selectBTaxStateLoaded, selectBTaxSubscriptionsEnabled, selectBTaxWatchListsEnabled } from '@btax/state/btax-state/btax-state.selectors';
import { selectBTaxAccount, selectBTaxAccountFriendlyTitle, selectBTaxAccountInCart, selectBTaxAccountIsPayable } from '@/app/modules/btax/state/entities/account/btax-account.selectors';
import { selectAppStateLoaded, selectUserAccountsEnabled } from '@/app/state/app-state/app-state.selectors';
import { State } from '@state/model';
import { Component, forwardRef, Inject, Input } from '@angular/core';
import { sync } from '@ces/sync';
import { Store } from '@ngrx/store';
import { Viewport } from '@shared/services/viewport.service';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { WatchListsDialogs } from '@watch-lists/services/watch-lists-dialogs.service';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'btax-account-actions',
    templateUrl: './btax-account-actions.component.html',
    styleUrls: ['./btax-account-actions.component.scss'],
})
export class BTaxAccountActionsComponent
{
    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly btaxCartService: BTaxCart,
        @Inject(forwardRef(() => WatchListsDialogs)) private readonly watchListDialogs: WatchListsDialogs,


        // Template dependencies

        protected viewport: Viewport,
    )
    {}


    // Customizations

    @Input() protected set accountNumber (value: string | undefined) { this.accountNumber$.next(value); }
    private readonly accountNumber$ = new BehaviorSubject<string | undefined>(undefined);

    @Input() protected color = '#888888';
    @Input() protected showPlaceholders = false;


    // Services state and settings

    private readonly appStateLoaded$ = this.store.select(selectAppStateLoaded);
    protected readonly accountsEnabled$ = this.store.select(selectUserAccountsEnabled);

    protected readonly servicesInfoLoaded$ = combineLatest(
        [this.appStateLoaded$, this.store.select(selectBTaxStateLoaded)]).pipe(map(
            ([appStateLoaded, pptStateLoaded]) => appStateLoaded && pptStateLoaded
        ));

    protected readonly displayPrintBillButton$ = this.store.select(selectBTaxDisplayPrintBillButton);


    // Block messages

    protected readonly blockMessages$ = this.store.select(selectBTaxBlockMessages);


    // Account info

    protected readonly account$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccount(accountNumber))
            : of(undefined)
        )
    );

    protected readonly accountIsPayable$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccountIsPayable(accountNumber))
            : of(undefined)
        )
    );

    protected readonly accountInCart$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.select(selectBTaxAccountInCart(accountNumber))
            : of(undefined)
        ),
    );


    // Cart

    protected readonly addToOrRemoveFromCartTitle$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.btaxCartService.getAddToOrRemoveFromCartTitle$(accountNumber)
            : of(undefined)
        )
    );

    protected readonly updatingCartState$ = new BehaviorSubject(false);

    protected toggleAccountInCart(e?: MouseEvent)
    {
        if (this.accountNumber$.value && !sync(this.account$)?.paymentBlocked && !this.updatingCartState$.value)
        {
            this.updatingCartState$.next(true);

            this.btaxCartService.toggleAccountInCart(
                this.accountNumber$.value,
                true,
                () => this.toggleAccountInCart(),
            ).subscribe({
                complete: () => this.updatingCartState$.next(false),
                error: () => this.updatingCartState$.next(false),
            });
        }
    }


    // Watch lists

    protected readonly watchListsEnabled$ = this.store.select(selectBTaxWatchListsEnabled);

    protected watchAccount()
    {
        const accountNumber = this.accountNumber$.value;

        if (accountNumber)
            this.watchListDialogs.watchAccount(
            {
                type: 'btax',
                id: accountNumber,
                humanId: sync(this.store.select(selectBTaxAccountFriendlyTitle(accountNumber)))!,
            });
    }


    // Subsctiptions

    protected readonly subscriptionsEnabled$ = this.store.select(selectBTaxSubscriptionsEnabled);

    protected showSubscriptionsDialog()
    {
        // const accountNumber = this.accountNumber$.value;

        // if (accountNumber)
        //     this.dialogsService.showSubscriptionsDialog(accountNumber);
    }


    // Auto-draft

    protected readonly autoDraftEnabled$ = this.store.select(selectBTaxAutoDraftEnabled);

    protected showAutoDraftDialog()
    {
        // const accountNumber = this.accountNumber$.value;

        // if (accountNumber)
        //     this.dialogsService.showAutoDraftDialog(accountNumber);
    }
}
